import React, { useState, useEffect } from "react";
import { listSocialAds, listSocialPosts, updateSocialAd, deleteSocialAd } from "../../../api";
import { useToast } from "../../../hooks/use-toast";
import AdModal from "./AdModal";
import { Plus, Rocket, Pause, Play, Trash2, PoundSterling, Eye, MousePointerClick, Target, Users } from "lucide-react";

const OBJ_LABEL = { traffic: "Traffic", leads: "Leads", engagement: "Engagement", awareness: "Awareness" };
const STATUS = {
  active: { l: "Active", c: "bg-green-100 text-green-800" },
  paused: { l: "Paused", c: "bg-yellow-100 text-yellow-800" },
  completed: { l: "Completed", c: "bg-gray-200 text-gray-600" },
  draft: { l: "Draft", c: "bg-gray-200 text-gray-600" },
};

const SocialAds = ({ onChange }) => {
  const [ads, setAds] = useState([]);
  const [published, setPublished] = useState([]);
  const [modal, setModal] = useState(false);
  const { toast } = useToast();

  const load = () => {
    listSocialAds().then(setAds).catch(() => setAds([]));
    listSocialPosts().then((p) => setPublished(p.filter((x) => x.status === "published"))).catch(() => setPublished([]));
  };
  useEffect(() => { load(); }, []);
  const refresh = () => { load(); onChange?.(); };

  const toggle = async (ad) => { try { await updateSocialAd(ad.id, { status: ad.status === "active" ? "paused" : "active" }); toast({ title: ad.status === "active" ? "Ad paused" : "Ad resumed" }); refresh(); } catch { toast({ title: "Could not update ad", variant: "destructive" }); } };
  const remove = async (id) => { if (!window.confirm("Delete this ad?")) return; try { await deleteSocialAd(id); refresh(); } catch { toast({ title: "Could not delete ad", variant: "destructive" }); } };

  return (
    <div data-testid="social-ads">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-gray-500">Turn any published post into a targeted Facebook/Instagram ad.</p>
        <button data-testid="new-ad-btn" onClick={() => setModal(true)} className="flex items-center gap-2 px-4 py-2.5 gpm-text-cream text-sm font-semibold" style={{ backgroundColor: "var(--gpm-green)" }}><Plus size={16} /> New ad</button>
      </div>

      {ads.length === 0 && <div className="py-16 text-center text-gray-400 border border-dashed border-black/15">No ads yet. Publish a post, then click <b>Make ad</b> — or use <b>New ad</b> here.</div>}

      <div className="space-y-4">
        {ads.map((a) => {
          const st = STATUS[a.status] || STATUS.draft;
          const m = a.metrics || {};
          const total = (a.dailyBudget || 0) * (a.durationDays || 0);
          return (
            <div key={a.id} data-testid={`ad-card-${a.id}`} className="bg-white border border-black/10 p-5">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <Rocket size={16} className="gpm-text-gold" />
                    <h4 className="font-semibold gpm-text-green">{a.name}</h4>
                    <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded ${st.c}`}>{st.l}</span>
                  </div>
                  <div className="flex flex-wrap gap-4 text-xs text-gray-500 mt-2">
                    <span className="flex items-center gap-1"><Target size={12} /> {OBJ_LABEL[a.objective] || a.objective}</span>
                    <span className="flex items-center gap-1"><Users size={12} /> {(a.locations || []).join(", ")} · {a.ageMin}–{a.ageMax}</span>
                    <span className="flex items-center gap-1"><PoundSterling size={12} /> £{a.dailyBudget}/day · {a.durationDays} days (£{total.toLocaleString()})</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  {a.status !== "completed" && <button data-testid={`toggle-${a.id}`} onClick={() => toggle(a)} className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 border border-black/20">{a.status === "active" ? <><Pause size={12} /> Pause</> : <><Play size={12} /> Resume</>}</button>}
                  <button data-testid={`ad-delete-${a.id}`} onClick={() => remove(a.id)} className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 border border-black/20 text-red-600"><Trash2 size={12} /></button>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 border-t border-black/5 pt-4">
                {[
                  { icon: Eye, l: "Impressions", v: (m.impressions || 0).toLocaleString() },
                  { icon: Users, l: "Reach", v: (m.reach || 0).toLocaleString() },
                  { icon: MousePointerClick, l: "Clicks", v: `${(m.clicks || 0).toLocaleString()} (${m.ctr || 0}%)` },
                  { icon: PoundSterling, l: "Spent", v: `£${(m.spend || 0).toLocaleString()}` },
                  { icon: Target, l: "Leads", v: m.leads || 0 },
                ].map((s) => {
                  const Icon = s.icon;
                  return (
                    <div key={s.l}>
                      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-gray-400 mb-0.5"><Icon size={11} /> {s.l}</div>
                      <div className="text-sm font-bold gpm-text-green">{s.v}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {modal && <AdModal publishedPosts={published} onClose={() => setModal(false)} onSaved={() => { setModal(false); refresh(); }} />}
    </div>
  );
};

export default SocialAds;
